import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Tabs } from 'antd';
import { withRouter } from 'utils/withRouter';
import { Button } from 'ui';
import SidePanel from 'common/side-panel/side-panel';
import { getAmountFilters } from 'utils/helpers';
import Filters from './filters';
import ColumnSidepanel from './column-sidepanel';
import { additionalTabTypes } from '../../utils/app-configs';
import RowSidepanel from './row-sidepanel';

const StyledButton = styled(Button)`
  text-transform: uppercase;
  margin-right: 20px;
  margin-top: 30px;
  margin-bottom: 5px;
`;

const { TabPane } = Tabs;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    font-size: 16px;
    font-weight: bold;
    text-transform: uppercase;
  }
  .tab-sub-false {
    overflow-y: auto;
    height: calc(100vh - (150px + ${p => p.level * 66}px)) !important;
    padding-left: 5px;
  }

  .ant-tabs-bar {
    margin: 0 !important;
  }
  .ant-tabs-tab {
    font-weight: 400;
    font-size: 16px;
    text-transform: uppercase;
    min-width: 110px;
    justify-content: center;
  }

  .ant-tabs-ink-bar {
    background: ${p => p.theme.colors.theme} !important;
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: ${p => p.theme.colors.theme} !important;
    }
  }
`;

const FiltersSidepanel = ({
  filters,
  sort,
  filterRef,
  isDataLoading,
  filterDefinitions,
  updateFilters,
  activeFilters,
  isPlanningBoard,
  intl,
  history,
  isGantt = false,
  filterGroups = [],
  worklistId,
  isTickets = false,
  relativeTimingRangeOptions,
  fixedFilterValues = {},
  additionalTabs = [],
}) => {
  const location = useLocation();
  const close = () => {
    // Don't use back() here because that cause to filters and sorting to be removed when closing
    const goBackPath = isGantt ?
      location.pathname.replace('/ganttfilterselect', '') :
      location.pathname.replace('/filterselect', '');
    history.push(goBackPath);
  };

  const resetFilters = () => {
    updateFilters([]);
  };

  const groupedFilterDefinitions = groupId =>
    filterDefinitions?.filter(fd => fd.filterGroupId === groupId);

  const getActiveFiltersCounter = (groupId) => {
    const amount = getAmountFilters(
      activeFilters,
      groupedFilterDefinitions(groupId),
      true,
      fixedFilterValues,
    );
    if (amount === 0) {
      return '';
    }
    if (amount > 9) {
      return '(9+)';
    }
    return `(${amount})`;
  };

  const renderTabs = (groups, baseId = '', level = 1) => <StyledTabs level={level}>
    {groups?.sort((a, b) => a.sort - b.sort)?.map(fg => <TabPane
      key={fg.id}
      tab={`${fg.description} ${getActiveFiltersCounter(fg.id)}`}
      className={`tab-sub-${!!fg.subFilterGroups?.length}`}
    >
      {fg.subFilterGroups?.length ?
        renderTabs(fg.subFilterGroups, fg.id, level + 1)
        :
        <Filters
          ref={filterRef}
          disabled={isDataLoading}
          filterDefinitions={
            filterDefinitions?.filter(fd =>
              fd.filterGroupId === `${baseId ? `${baseId}.` : ''}${fg.id}`)
          }
          updateFilters={updateFilters}
          activeFilters={activeFilters}
          isPlanningBoard={isPlanningBoard}
          filters={filters}
          sort={sort}
          worklistId={worklistId}
          isTickets={isTickets}
          relativeTimingRangeOptions={relativeTimingRangeOptions}
          fixedFilterValues={fixedFilterValues}
        />
      }
      <StyledButton type='default' onClick={close} disabled={isDataLoading}>
        {intl.formatMessage({ id: 'worklist.filters.CLOSE' })}
      </StyledButton>
    </TabPane>,
    )}
  </StyledTabs>;

  const renderAdditionalTabs = (type) => {
    switch (type) {
      case additionalTabTypes.columns:
        return <ColumnSidepanel />;
      case additionalTabTypes.rows:
        return <RowSidepanel />;
      default:
        return null;
    }
  };

  const getSidepanelTitle = () => {
    const key = additionalTabs?.map(t => t.toUpperCase()).join('_') || '';
    return intl.formatMessage({
      id: `worklist.filters.EDIT_FILTERS${key ? `_${key}` : ''}`,
    });
  };

  return (
    <SidePanel
      width={'40%'}
      title={getSidepanelTitle()}
      onPanelClose={close}
      scrollOnOverflow={!filterGroups?.length}
    >
      <StyledTabs
        level={0}
        defaultActiveKey={location?.state?.openTabId || 'filters'}
      >
        <TabPane
          key='filters'
          tab={intl.formatMessage({ id: 'worklist.filters.FILTERS' })}
        >
          {!filterGroups?.length ?
            <>
              <Filters
                ref={filterRef}
                disabled={isDataLoading}
                filterDefinitions={filterDefinitions}
                updateFilters={updateFilters}
                activeFilters={activeFilters}
                isPlanningBoard={isPlanningBoard}
                filters={filters}
                sort={sort}
                worklistId={worklistId}
                isTickets={isTickets}
                relativeTimingRangeOptions={relativeTimingRangeOptions}
                fixedFilterValues={fixedFilterValues}
              />
              <>
                <StyledButton
                  data-test-id='filters-sidepanel.reset'
                  type='default'
                  onClick={resetFilters}
                  disabled={!activeFilters || !Object.keys(activeFilters)?.length || isDataLoading}
                >
                  {intl.formatMessage({ id: 'worklist.filters.RESET_FILTERS' })}
                </StyledButton>
                <StyledButton type='default' onClick={close} disabled={isDataLoading}>
                  {intl.formatMessage({ id: 'worklist.filters.CLOSE' })}
                </StyledButton>
              </>
            </> :
            renderTabs(filterGroups)
          }
        </TabPane>
        {additionalTabs?.map(t =>
          <TabPane
            key={t}
            tab={intl.formatMessage({ id: `worklist.filters.${t.toUpperCase()}` })}
          >
            {renderAdditionalTabs(t)}
            <StyledButton type='default' onClick={close} disabled={isDataLoading}>
              {intl.formatMessage({ id: 'worklist.filters.CLOSE' })}
            </StyledButton>
          </TabPane>,
        )}
      </StyledTabs>
    </SidePanel>
  );
};

FiltersSidepanel.propTypes = {
  filterRef: PropTypes.object,
  isDataLoading: PropTypes.bool,
  filterDefinitions: PropTypes.array,
  updateFilters: PropTypes.func,
  activeFilters: PropTypes.array,
  isPlanningBoard: PropTypes.bool,
  intl: PropTypes.object,
  history: PropTypes.object,
  filterGroups: PropTypes.array,
  filters: PropTypes.string,
  sort: PropTypes.string,
  worklistId: PropTypes.string,
  isTickets: PropTypes.bool,
  relativeTimingRangeOptions: PropTypes.object,
  fixedFilterValues: PropTypes.object,
  additionalTabs: PropTypes.array,
  isGantt: PropTypes.bool,
};

export default withRouter(injectIntl(FiltersSidepanel));
